import { CookiePreferences as GeneratedCookiePreferences } from '@/generated/graphql';

export type CookieCategory = 'functional' | 'analytics' | 'marketing';

// Use the generated type instead of local interface
export type CookiePreferences = GeneratedCookiePreferences;

export interface CookieConsentState {
  hasConsented: boolean;
  preferences: CookiePreferences;
}

const COOKIE_CONSENT_KEY = 'happiscreen_cookie_consent';
const CONSENT_EXPIRY_DAYS = 365; // 1 year

export const defaultPreferences: CookiePreferences = {
  functional: true, // Always required for basic functionality
  analytics: false,
  marketing: false,
  consentDate: null,
};

export const loadCookieConsent = (): CookieConsentState | null => {
  try {
    const stored = localStorage.getItem(COOKIE_CONSENT_KEY);
    if (!stored) return null;

    const parsed = JSON.parse(stored) as CookieConsentState;

    // Check if consent is expired
    if (parsed.preferences.consentDate) {
      const consentDate = new Date(parsed.preferences.consentDate);
      const expiryDate = new Date(consentDate);
      expiryDate.setDate(expiryDate.getDate() + CONSENT_EXPIRY_DAYS);

      if (new Date() > expiryDate) {
        localStorage.removeItem(COOKIE_CONSENT_KEY);
        return null;
      }
    }

    return parsed;
  } catch {
    return null;
  }
};

export const saveCookieConsent = (preferences: CookiePreferences): void => {
  const consent: CookieConsentState = {
    hasConsented: true,
    preferences: {
      ...preferences,
      consentDate: preferences.consentDate || new Date().toISOString(),
    },
  };

  localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify(consent));
};

export const clearCookieConsent = (): void => {
  localStorage.removeItem(COOKIE_CONSENT_KEY);
};

// Utility to check if a specific category is consented
export const isCategoryConsented = (category: CookieCategory): boolean => {
  const consent = loadCookieConsent();
  if (!consent) return false;
  return consent.preferences[category] ?? false;
};

// Script loader that respects consent
export const loadScriptWithConsent = (
  src: string,
  category: CookieCategory,
  attributes?: Record<string, string>,
): Promise<void> => {
  return new Promise((resolve, reject) => {
    if (!isCategoryConsented(category)) {
      reject(new Error(`Consent not given for ${category} cookies`));
      return;
    }

    const script = document.createElement('script');
    script.src = src;
    script.async = true;

    if (attributes) {
      Object.entries(attributes).forEach(([key, value]) => {
        script.setAttribute(key, value);
      });
    }

    script.onload = () => resolve();
    script.onerror = () => reject(new Error(`Failed to load script: ${src}`));

    document.head.appendChild(script);
  });
};
